import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  MenuItem,
  Select,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import DatePicker from "~/components/common/forms/DatePicker";;
import FilterGridRow from "./FilterGridRow";
import { useState } from "react";
import { useOrgConfig, OrgConfig } from "~/providers/OrgConfigProvider";
import { sortBy } from "lodash";
import VisitTypeDuration, { updateDuration } from "../VisitTypeDuration";
import { useSelectableServiceLineIds } from "~/hooks/useDefaultServiceLines";
import { PatientDetailQuery, SchedulerOrganizationalUnitsQuery } from "~/graphql/__generated__/graphql";
import { getSchedulingOrganizationalUnitId } from "~/utils/visitsHelper";

export type SchedulerParametersDialogInput = {
  serviceLineId: string;
  organizationalUnitId?: number;
  visitTypeId: string;
  serviceIds: string[];
  duration: string;
  // This is a hidden value that is in the form for easier data passing
  // and storing "last applied" state so the cancel button can work properly
  durationLocked: boolean;
  serviceNames: string[];
  targetTimeFrom: Date;
  targetTime: Date;
};

type SchedulerParametersDialogProps = {
  values: SchedulerParametersDialogInput,
  onClose: () => void;
  onSubmit: (values: SchedulerParametersDialogInput) => void;
  rescheduling: boolean;
  dialogWarning: string | null;
  episodes: Pick<NonNullable<PatientDetailQuery["patientSingle"]>["episodes"][number], "active" | "serviceLineId">[];
  organizationalUnits?: SchedulerOrganizationalUnitsQuery["organizationalUnit"];
  requireExplicitOrganizationalUnitSelection?: boolean;
};

export const setServiceLine = (serviceLineId: string, values: SchedulerParametersDialogInput, orgConfig: OrgConfig): SchedulerParametersDialogInput => {
  // If the selected visit type is also in the new service line, then we don't need to clear it out.
  const newSL = orgConfig.serviceLines[serviceLineId];
  const isVTInNewServiceLine = newSL?.visitTypeServiceLines.find(vtsl => vtsl.visitTypeId.toString() === values.visitTypeId);

  return {...values, serviceLineId,
    organizationalUnitId: serviceLineId === values.serviceLineId ? values.organizationalUnitId : undefined,
    ...updateDuration({
    duration: values.duration,
    durationLocked: values.durationLocked,
    visitTypeId: isVTInNewServiceLine ? values.visitTypeId : "",
    serviceIds: isVTInNewServiceLine ? values.serviceIds : [],
    serviceNames: isVTInNewServiceLine ? values.serviceNames : [],
  }, orgConfig)}
}

export default function SchedulerParametersDialog({
  values,
  onClose,
  onSubmit,
  rescheduling,
  dialogWarning,
  episodes,
  organizationalUnits,
  requireExplicitOrganizationalUnitSelection = false,
}: SchedulerParametersDialogProps) {
  const orgConfig = useOrgConfig();
  const serviceLines = useSelectableServiceLineIds()
    .map(id => orgConfig.serviceLines[id])
    .filter(sl => sl !== undefined);

  const [local, setLocal] = useState(values);
  const creatingEpisode = !rescheduling && !episodes.some((episode) =>
    episode.active && episode.serviceLineId === Number(local.serviceLineId)
  );
  const organizationalUnitId = getSchedulingOrganizationalUnitId(undefined, organizationalUnits ?? [], local.organizationalUnitId, !requireExplicitOrganizationalUnitSelection);
  const requiresOrganizationalUnit = organizationalUnits !== undefined && creatingEpisode;

  return (
    <Dialog
      open={true}
      onClose={onClose}
      slotProps={{ paper: { sx: { p: 2 }}}}
    >
      <DialogContent>
        {dialogWarning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {dialogWarning}
          </Alert>
        )}
        <Grid container rowSpacing={3}>
          <FilterGridRow label="Service Line" loading={orgConfig.loading}>
            <FormControl variant="standard" fullWidth>
              <Select fullWidth disabled={rescheduling} value={local.serviceLineId} onChange={(e) => {
                setLocal(setServiceLine(e.target.value, local, orgConfig));
              }}>
                {sortBy(serviceLines, "name").map(sl => (
                  <MenuItem key={sl.id} value={sl.id.toString()}>
                    {sl.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FilterGridRow>

          <VisitTypeDuration values={local} setValues={values => setLocal({...local, ...values })} serviceLineId={local.serviceLineId} />

          {requiresOrganizationalUnit && (organizationalUnits.length > 1 || requireExplicitOrganizationalUnitSelection) && (
            <FilterGridRow label="Organizational Unit">
              <FormControl variant="standard" fullWidth required>
                <Select
                  fullWidth
                  value={local.organizationalUnitId ?? ""}
                  onChange={(event) => setLocal({ ...local, organizationalUnitId: Number(event.target.value) })}
                >
                  {organizationalUnits.map((unit) => (
                    <MenuItem key={unit.id} value={unit.id}>{unit.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </FilterGridRow>
          )}

          <FilterGridRow label="Target Date" loading={orgConfig.loading}>
            <FormControl variant="standard" fullWidth>
              <DatePicker
                slotProps={{
                  textField: { variant: "standard" },
                }}
                value={local.targetTime}
                onChange={(targetTime) => targetTime && setLocal({ ...local, targetTime })}
              />
            </FormControl>
          </FilterGridRow>
        </Grid>

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          type="submit"
          disabled={local.duration == "0" || !local.serviceLineId || !local.visitTypeId || (requiresOrganizationalUnit && organizationalUnitId == null)}
          onClick={() => { onSubmit(requiresOrganizationalUnit ? { ...local, organizationalUnitId } : local); onClose(); }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
